<template>
    <div
        class="gallery-layout-module gallery-layout-module--grid"
        :class="dynamicClassList"
        :style="galleryGridVars"
    >
        <slot
            v-for="(item, index) in items"
            :key="item.media?.url || index"
            name="gallery-item"
            :item="item"
            :index="index"
            :scroll-hint-priority="index"
            :item-style="getGridCellStyle(index)"
        />
    </div>
</template>

<script>
import {GalleryLayout} from "@/scripts/common/enums.js";

export default {
    name: "GalleryGrid",
    props: {
        section: {
            type: Object,
            default: null
        },
        items: {
            type: Array,
            default: () => []
        },
        pendingFocusIndex: {
            type: Number,
            default: null
        },
        isPreview: {
            type: Boolean,
            default: false
        }
    },
    emits: ['gallery-item-focus-applied'],
    watch: {
        items() {
            this.focusPendingItem();
        },
        pendingFocusIndex() {
            this.focusPendingItem();
        }
    },
    mounted() {
        this.focusPendingItem();
    },
    computed: {
        isOffsetGrid() {
            return this.section?.layout === GalleryLayout.OFFSET_GRID;
        },
        dynamicClassList() {
            return [
                {'gallery-layout-module--offset-grid' : this.isOffsetGrid},
                this.noMockupOffsetClass,
                this.mockupTypeClass
            ]
        },
        activeDeviceType() {
            return this.items[0]?.media?.mockupSettings?.deviceType || null;
        },
        mockupTypeClass() {
            return this.activeDeviceType ? this.activeDeviceType.toLowerCase() : "no-mockup";
        },
        columns() {
            return this.section?.columns || 3;
        },
        noMockupOffsetClass() {
            return this.activeDeviceType ? null : `no-mockup-cols-${this.columns}`;
        },
        galleryGridVars() {
            const colsByDeviceType = {
                Mobile: 4,
                Tablet: 3
            };
            const offsetColumns = this.activeDeviceType ? (colsByDeviceType[this.activeDeviceType] || 2) : this.columns;
            return {
                '--gallery-grid-cols': this.columns,
                '--gallery-offset-grid-cols': offsetColumns
            };
        }
    },
    methods: {
        focusPendingItem() {
            const targetIndex = this.pendingFocusIndex;
            if (!Number.isInteger(targetIndex)) return;
            if (targetIndex < 0 || targetIndex >= this.items.length) return;

            this.$nextTick(() => {
                const targetEl = this.$el?.children?.[targetIndex];
                if (!targetEl) return;

                targetEl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
                this.$emit('gallery-item-focus-applied', targetIndex);
            });
        },
        getGridCellStyle(index) {
            if (!this.isOffsetGrid) return null;

            const columns = Number(this.galleryGridVars['--gallery-offset-grid-cols']) || 2;
            const cycleIndex = index % 4;
            const cycleRow = Math.floor(index / 4) + 1;

            if (columns === 4) {
                const columnByCycleIndex = [3, 1, 4, 2];
                return {
                    gridColumn: columnByCycleIndex[cycleIndex],
                    gridRow: cycleRow
                };
            }

            if (columns === 3) {
                const columnByCycleIndex = [3, 1, 2];
                const offsetCycleIndex = index % 3;
                const row = Math.floor(index / 3) + 1;
                return {
                    gridColumn: columnByCycleIndex[offsetCycleIndex],
                    gridRow: row
                };
            }

            const column = (index % columns) + 1;
            const row = Math.floor(index / columns) + 1;
            return {
                gridColumn: column,
                gridRow: row
            };
        }
    }
}
</script>

<style scoped lang="scss">
@import "@/styles/components/sections/modules/gallery_grid.scss";
</style>
